import { 
  SplitTableSession, 
  SplitTablePerson, 
  SplitTableItem, 
  SplitPayment, 
  SplitTableReport,
  SplitTableAnalytics,
  SplitTableSettings 
} from '../types/splitTable';

export class SplitTableService {
  private static instance: SplitTableService;
  private sessions: Map<string, SplitTableSession> = new Map();
  private reports: SplitTableReport[] = [];
  private settings: SplitTableSettings = {
    allowCustomSplit: true,
    maxSplitCount: 8,
    defaultPaymentMethods: ['cash', 'card'],
    requireCustomerInfo: false,
    autoCalculateTax: true,
    autoCalculateTip: true,
    defaultTipPercentage: 15,
    allowPartialPayments: true,
    requireStaffConfirmation: true
  };

  static getInstance(): SplitTableService {
    if (!SplitTableService.instance) {
      SplitTableService.instance = new SplitTableService();
    }
    return SplitTableService.instance;
  }

  // Create a new split table session
  createSplitSession(tableId: number, tableNumber: string, orderId: string, originalTotal: number, createdBy: string): SplitTableSession {
    const sessionId = `split_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const session: SplitTableSession = {
      id: sessionId,
      tableId,
      tableNumber,
      orderId,
      originalOrderTotal: originalTotal,
      splitCount: 0,
      splits: [],
      status: 'active',
      createdAt: new Date(),
      createdBy,
      notes: ''
    };

    this.sessions.set(sessionId, session);
    return session;
  }

  // Add a person to the split
  addPersonToSplit(sessionId: string, personName: string, email?: string, phone?: string): SplitTablePerson {
    const session = this.sessions.get(sessionId);
    if (!session) {
      throw new Error('Split session not found');
    }

    if (session.splits.length >= this.settings.maxSplitCount) {
      throw new Error(`Maximum split count of ${this.settings.maxSplitCount} reached`);
    }

    const personId = `person_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const person: SplitTablePerson = {
      id: personId,
      name: personName,
      email,
      phone,
      items: [],
      subtotal: 0,
      tax: 0,
      tip: 0,
      total: 0,
      paymentStatus: 'pending',
      payments: []
    };

    session.splits.push(person);
    session.splitCount = session.splits.length;
    
    this.sessions.set(sessionId, session);
    return person;
  }

  // Assign items to a person
  assignItemsToPerson(sessionId: string, personId: string, items: SplitTableItem[]): void {
    const session = this.sessions.get(sessionId);
    if (!session) {
      throw new Error('Split session not found');
    }

    const person = session.splits.find(p => p.id === personId);
    if (!person) {
      throw new Error('Person not found in split session');
    }

    // Remove items from other people first
    items.forEach(item => {
      session.splits.forEach(p => {
        p.items = p.items.filter(i => i.id !== item.id);
      });
    });

    // Add items to the person
    person.items = [...person.items, ...items];
    
    // Recalculate totals
    this.recalculatePersonTotals(sessionId, personId);
    this.sessions.set(sessionId, session);
  }

  // Recalculate totals for a person
  private recalculatePersonTotals(sessionId: string, personId: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;

    const person = session.splits.find(p => p.id === personId);
    if (!person) return;

    person.subtotal = person.items.reduce((sum, item) => sum + item.totalPrice, 0);
    
    if (this.settings.autoCalculateTax) {
      person.tax = person.subtotal * 0.19; // 19% VAT for Germany
    }
    
    if (this.settings.autoCalculateTip) {
      person.tip = person.subtotal * (this.settings.defaultTipPercentage / 100);
    }
    
    person.total = person.subtotal + person.tax + person.tip;
    
    this.sessions.set(sessionId, session);
  }

  // Process payment for a person
  processPayment(sessionId: string, personId: string, payment: Omit<SplitPayment, 'id' | 'splitId' | 'processedAt'>): SplitPayment {
    const session = this.sessions.get(sessionId);
    if (!session) {
      throw new Error('Split session not found');
    }

    const person = session.splits.find(p => p.id === personId);
    if (!person) {
      throw new Error('Person not found in split session');
    }

    const paymentId = `payment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fullPayment: SplitPayment = {
      ...payment,
      id: paymentId,
      splitId: personId,
      processedAt: new Date()
    };

    person.payments.push(fullPayment);
    
    // Update payment status
    const totalPaid = person.payments.reduce((sum, p) => sum + p.totalAmount, 0);
    if (totalPaid >= person.total) {
      person.paymentStatus = 'paid';
    } else if (totalPaid > 0) {
      person.paymentStatus = 'partial';
    }

    this.sessions.set(sessionId, session);
    return fullPayment;
  }

  // Complete the split session
  completeSplitSession(sessionId: string): SplitTableSession {
    const session = this.sessions.get(sessionId);
    if (!session) {
      throw new Error('Split session not found');
    }

    // Check if all people have paid
    const allPaid = session.splits.every(person => person.paymentStatus === 'paid');
    if (!allPaid) {
      throw new Error('Cannot complete split session - not all payments are complete');
    }

    session.status = 'completed';
    session.completedAt = new Date();

    // Generate report
    this.generateReport(session);

    this.sessions.set(sessionId, session);
    return session;
  }

  // Generate report for completed session
  private generateReport(session: SplitTableSession): void {
    const processingTime = session.completedAt 
      ? (session.completedAt.getTime() - session.createdAt.getTime()) / (1000 * 60) // minutes
      : 0;

    const paymentMethods: Record<string, number> = {};
    session.splits.forEach(person => {
      person.payments.forEach(payment => {
        paymentMethods[payment.paymentMethod] = (paymentMethods[payment.paymentMethod] || 0) + payment.totalAmount;
      });
    });

    const report: SplitTableReport = {
      id: `report_${session.id}`,
      sessionId: session.id,
      tableNumber: session.tableNumber,
      totalAmount: session.splits.reduce((sum, person) => sum + person.total, 0),
      splitCount: session.splitCount,
      averagePerPerson: session.splits.reduce((sum, person) => sum + person.total, 0) / session.splitCount,
      paymentMethods,
      processingTime,
      staffMember: session.createdBy,
      createdAt: session.createdAt,
      completedAt: session.completedAt!
    };

    this.reports.push(report);
  }

  // Get split session by ID
  getSplitSession(sessionId: string): SplitTableSession | undefined {
    return this.sessions.get(sessionId);
  }

  // Get all active split sessions
  getActiveSplitSessions(): SplitTableSession[] {
    return Array.from(this.sessions.values()).filter(session => session.status === 'active');
  }

  // Get split reports
  getSplitReports(startDate?: Date, endDate?: Date): SplitTableReport[] {
    if (!startDate && !endDate) {
      return this.reports;
    }

    return this.reports.filter(report => {
      if (startDate && report.createdAt < startDate) return false;
      if (endDate && report.createdAt > endDate) return false;
      return true;
    });
  }

  // Get analytics
  getAnalytics(startDate?: Date, endDate?: Date): SplitTableAnalytics {
    const reports = this.getSplitReports(startDate, endDate);
    
    const totalSessions = reports.length;
    const totalRevenue = reports.reduce((sum, report) => sum + report.totalAmount, 0);
    const averageSplitCount = totalSessions > 0 ? reports.reduce((sum, report) => sum + report.splitCount, 0) / totalSessions : 0;
    const averageProcessingTime = totalSessions > 0 ? reports.reduce((sum, report) => sum + report.processingTime, 0) / totalSessions : 0;

    // Payment methods analysis
    const paymentMethodCounts: Record<string, number> = {};
    reports.forEach(report => {
      Object.entries(report.paymentMethods).forEach(([method, _amount]) => {
        paymentMethodCounts[method] = (paymentMethodCounts[method] || 0) + 1;
      });
    });

    const mostUsedPaymentMethods = Object.entries(paymentMethodCounts)
      .map(([method, count]) => ({
        method,
        count,
        percentage: (count / totalSessions) * 100
      }))
      .sort((a, b) => b.count - a.count);

    // Staff performance
    const staffStats: Record<string, { count: number; totalTime: number }> = {};
    reports.forEach(report => {
      if (!staffStats[report.staffMember]) {
        staffStats[report.staffMember] = { count: 0, totalTime: 0 };
      }
      staffStats[report.staffMember].count++;
      staffStats[report.staffMember].totalTime += report.processingTime;
    });

    const staffPerformance = Object.entries(staffStats).map(([staffId, stats]) => ({
      staffId,
      staffName: staffId, // In real implementation, you'd fetch actual staff names
      sessionsCount: stats.count,
      averageProcessingTime: stats.totalTime / stats.count
    }));

    // Hourly distribution
    const hourlyStats: Record<number, number> = {};
    reports.forEach(report => {
      const hour = report.createdAt.getHours();
      hourlyStats[hour] = (hourlyStats[hour] || 0) + 1;
    });

    const hourlyDistribution = Object.entries(hourlyStats)
      .map(([hour, count]) => ({ hour: parseInt(hour), count }))
      .sort((a, b) => a.hour - b.hour);

    // Daily distribution
    const dailyStats: Record<string, { count: number; revenue: number }> = {};
    reports.forEach(report => {
      const date = report.createdAt.toISOString().split('T')[0];
      if (!dailyStats[date]) {
        dailyStats[date] = { count: 0, revenue: 0 };
      }
      dailyStats[date].count++;
      dailyStats[date].revenue += report.totalAmount;
    });

    const dailyDistribution = Object.entries(dailyStats)
      .map(([date, stats]) => ({ date, ...stats }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return {
      totalSessions,
      totalRevenue,
      averageSplitCount,
      averageProcessingTime,
      mostUsedPaymentMethods,
      staffPerformance,
      hourlyDistribution,
      dailyDistribution
    };
  }

  // Update settings
  updateSettings(newSettings: Partial<SplitTableSettings>): void {
    this.settings = { ...this.settings, ...newSettings };
  }

  // Get settings
  getSettings(): SplitTableSettings {
    return { ...this.settings };
  }

  // Cancel split session
  cancelSplitSession(sessionId: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) {
      throw new Error('Split session not found');
    }

    session.status = 'cancelled';
    this.sessions.set(sessionId, session);
  }
}
